// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useAcceptCollateralAgreementChangeRequest } from '@hooks/mutations/collateral/collateralAgreementChangeRequests/useAcceptCollateralAgreementChangeRequest';
import { useCancelCollateralAgreementChangeRequest } from '@hooks/mutations/collateral/collateralAgreementChangeRequests/useCancelCollateralAgreementChangeRequest';
import { useRejectCollateralAgreementChangeRequest } from '@hooks/mutations/collateral/collateralAgreementChangeRequests/useRejectCollateralAgreementChangeRequest';
import { useAcceptCollateralAgreementRequest } from '@hooks/mutations/collateral/collateralAgreements/useAcceptCollateralAgreementRequest';
import { useCancelCollateralAgreementRequest } from '@hooks/mutations/collateral/collateralAgreements/useCancelCollateralAgreementRequest';
import { useRejectCollateralAgreementRequest } from '@hooks/mutations/collateral/collateralAgreements/useRejectCollateralAgreementRequest';
import { useTerminateCollateralAgreement } from '@hooks/mutations/collateral/collateralAgreements/useTerminateCollateralAgreement';
import { useCollateralStateV2 } from '@hooks/queries/collateralv2/collateral/useCollateralStateV2';
import { useCollateralUserServiceV2 } from '@hooks/queries/collateralv2/onboarding/useUserService';
import React from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import {
  CollateralAgreement,
  CollateralAgreementChangeRequest,
  CollateralAgreementRequest,
} from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Model/Collateral';
import { ContractId } from '@daml/types';
import { useModuleParty } from '../../../contexts/ModuleContext';
import {
  ERR_ACC_AGR_REQ,
  ERR_ACC_CHANGE_REQ,
  ERR_CAN_AGR_REQ,
  ERR_REJ_AGR_REQ,
  ERR_REJ_CHANGE_REQ,
} from '../../../utils/strings';
import { CollateralAgreementRow } from '../obligations/types';
import { GenericAction } from '../types';

type Action = GenericAction<CollateralAgreementRow>;

interface FilterCollateralStateArgs {
  partyA: string;
  partyB: string;
  operator: string;
  id: string;
}

export const useCreateAgreementActions = () => {
  const party = useModuleParty();
  const userService = useCollateralUserServiceV2();
  const collateralStates = useCollateralStateV2();

  const { showBoundary } = useErrorBoundary<Error>();
  const [dialogName, setDialogName] = React.useState('');
  const [onTerminate, setOnTerminate] = React.useState<(() => Promise<unknown>) | undefined>();
  const terminate = useTerminateCollateralAgreement();
  const acceptCollateralAgreementRequest = useAcceptCollateralAgreementRequest();
  const rejectCollateralAgreementRequest = useRejectCollateralAgreementRequest();
  const cancelCollateralAgreementRequest = useCancelCollateralAgreementRequest();
  const acceptCollateralAgreementChangeRequest = useAcceptCollateralAgreementChangeRequest();
  const cancelCollateralAgreementChangeRequest = useCancelCollateralAgreementChangeRequest();
  const rejectCollateralAgreementChangeRequest = useRejectCollateralAgreementChangeRequest();

  const acceptChangeRequest = React.useCallback(
    (row: CollateralAgreementRow) => {
      if (!userService.data)
        return Promise.resolve(
          showBoundary(new Error(ERR_ACC_CHANGE_REQ, { cause: 'User service is not available.' })),
        );

      return acceptCollateralAgreementChangeRequest({
        cid: userService.data.contractId,
        argument: {
          cid: row.contractId as ContractId<CollateralAgreementChangeRequest>,
          payload: {
            actor: party,
          },
        },
      });
    },
    [acceptCollateralAgreementChangeRequest, party, showBoundary, userService.data],
  );
  const cancelChangeRequest = React.useCallback(
    (row: CollateralAgreementRow) => {
      if (!userService.data)
        return Promise.resolve(
          showBoundary(new Error(ERR_CAN_AGR_REQ, { cause: 'User service is not available.' })),
        );

      return cancelCollateralAgreementChangeRequest({
        cid: userService.data.contractId,

        argument: {
          cid: row.contractId as ContractId<CollateralAgreementChangeRequest>,
          payload: {
            actor: party,
          },
        },
      });
    },
    [cancelCollateralAgreementChangeRequest, party, showBoundary, userService.data],
  );
  const rejectChangeRequest = React.useCallback(
    (r: CollateralAgreementRow, reason: string) => {
      if (!userService.data)
        return Promise.resolve(
          showBoundary(new Error(ERR_REJ_CHANGE_REQ, { cause: 'User service is not available.' })),
        );

      return rejectCollateralAgreementChangeRequest({
        cid: userService.data.contractId,
        argument: {
          cid: r.contractId as ContractId<CollateralAgreementChangeRequest>,
          payload: { reason },
        },
      });
    },
    [userService.data, rejectCollateralAgreementChangeRequest, showBoundary],
  );
  const terminateCollateralAgreement = React.useCallback(
    (row: CollateralAgreementRow) => {
      if (!userService.data)
        return Promise.resolve(
          showBoundary(new Error(ERR_ACC_AGR_REQ, { cause: 'User service is not available.' })),
        );
      const filterCollateralStates = (args: FilterCollateralStateArgs) => {
        return (collateralStates.data || []).filter(
          (cs) =>
            cs.payload.partyA === args.partyA &&
            cs.payload.partyB === args.partyB &&
            cs.payload.id === args.id &&
            cs.payload.operator === args.operator,
        );
      };
      const [collateralStateCid] = filterCollateralStates({
        operator: row.payload.operator,
        id: row.payload.id,
        partyA: row.payload.requestor,
        partyB: row.payload.counterparty,
      });

      return terminate({
        cid: userService.data.contractId,
        argument: {
          cid: row.contractId as ContractId<CollateralAgreement>,
          payload: {
            actor: party,
            collateralStateCid: collateralStateCid.contractId,
          },
        },
      });
    },
    [party, terminate, showBoundary, userService.data, collateralStates.data],
  );
  const [onReject, setOnReject] = React.useState<
    ((reason: string) => Promise<unknown>) | undefined
  >();
  const acceptAgreement = React.useCallback(
    (r: CollateralAgreementRow) => {
      if (!userService.data)
        return Promise.resolve(
          showBoundary(new Error(ERR_ACC_AGR_REQ, { cause: 'User service is not available.' })),
        );

      return acceptCollateralAgreementRequest({
        cid: userService.data.contractId,
        argument: {
          cid: r.contractId as ContractId<CollateralAgreementRequest>,
          payload: {},
        },
      });
    },
    [userService.data, acceptCollateralAgreementRequest, showBoundary],
  );

  const rejectAgreement = React.useCallback(
    (r: CollateralAgreementRow, reason: string) => {
      if (!userService.data)
        return Promise.resolve(
          showBoundary(new Error(ERR_REJ_AGR_REQ, { cause: 'User service is not available.' })),
        );

      return rejectCollateralAgreementRequest({
        cid: userService.data.contractId,
        argument: {
          cid: r.contractId as ContractId<CollateralAgreementRequest>,
          payload: { reason },
        },
      });
    },
    [userService.data, rejectCollateralAgreementRequest, showBoundary],
  );

  const cancelAgreement = React.useCallback(
    (r: CollateralAgreementRow) => {
      if (!userService.data)
        return Promise.resolve(
          showBoundary(new Error(ERR_CAN_AGR_REQ, { cause: 'User service is not available.' })),
        );

      return cancelCollateralAgreementRequest({
        cid: userService.data.contractId,
        argument: {
          cid: r.contractId as ContractId<CollateralAgreementRequest>,
          payload: {},
        },
      });
    },
    [userService.data, cancelCollateralAgreementRequest, showBoundary],
  );

  const isUser = !!userService.data;

  const createCollateralActions = (r: CollateralAgreementRow) => {
    let actions: Action[] = [];

    const isSideOfCollateralAgreement = [r.payload.partyA, r.payload.partyB].includes(party);

    if (r.state === 'Change Requested') {
      if (r.payload.requestor === party) {
        actions = actions.concat([
          {
            name: 'Cancel',
            action: cancelChangeRequest,
            buttonProps: { variant: 'outlined', color: 'error' },
          },
          {
            inMoreMenu: true,
            name: 'View details',
            buttonProps: {
              variant: 'outlined',
            },
            action: () => {
              setDialogName('details');
              return Promise.resolve();
            },
          },
        ]);
      }
      if (r.payload.counterparty === party) {
        actions = actions.concat([
          { name: 'Accept', action: acceptChangeRequest },
          {
            name: 'Reject',
            buttonProps: {
              variant: 'outlined',
              color: 'error',
            },
            action: () => {
              setOnReject(() => (reason: string) => rejectChangeRequest(r, reason));
              return Promise.resolve();
            },
          },
          {
            inMoreMenu: true,
            name: 'View details',
            buttonProps: {
              variant: 'outlined',
            },
            action: () => {
              setDialogName('details');
              return Promise.resolve();
            },
          },
        ]);
      }
    }
    if (r.state === 'Accepted') {
      actions = actions.concat([
        {
          inMoreMenu: true,
          name: 'View details',
          action: () => {
            setDialogName('details');
            return Promise.resolve();
          },
          buttonProps: { variant: 'outlined' },
        },
        {
          inMoreMenu: true,
          name: 'Settlement history',
          action: () => {
            setDialogName('history');
            return Promise.resolve();
          },
          buttonProps: { variant: 'outlined' },
        },
      ]);

      if (isSideOfCollateralAgreement) {
        actions = actions.concat([
          {
            inMoreMenu: true,
            name: 'Modify agreement',
            action: () => {
              setDialogName('modify');
              return Promise.resolve();
            },
            buttonProps: { variant: 'outlined' },
          },
          {
            inMoreMenu: true,
            name: 'Terminate agreement',
            action: () => {
              setDialogName('terminate');
              setOnTerminate(
                () => () => terminateCollateralAgreement(r).then(() => setDialogName('')),
              );
              return Promise.resolve();
            },
            buttonProps: { variant: 'outlined', color: 'error' },
            color: 'error',
          },
        ]);
      }
    }
    if (isUser && r.state === 'Pending') {
      if (r.payload.counterparty === party) {
        actions = actions.concat([
          { name: 'Accept', action: acceptAgreement },
          {
            name: 'Reject',
            buttonProps: {
              variant: 'outlined',
            },
            action: () => {
              setOnReject(() => (reason: string) => rejectAgreement(r, reason));
              return Promise.resolve();
            },
          },
          {
            inMoreMenu: true,
            name: 'View details',
            buttonProps: {
              variant: 'outlined',
            },
            action: () => {
              setDialogName('details');
              return Promise.resolve();
            },
          },
        ]);
      }
      if (r.payload.requestor === party) {
        actions = actions.concat([
          { name: 'Cancel', action: cancelAgreement, buttonProps: { variant: 'outlined' } },
          {
            inMoreMenu: true,
            name: 'View details',
            buttonProps: {
              variant: 'outlined',
            },
            action: () => {
              setDialogName('details');
              return Promise.resolve();
            },
          },
        ]);
      }
    }

    return actions;
  };
  return {
    createCollateralActions,
    onReject,
    setOnReject,
    onTerminate,
    setOnTerminate,
    dialogName,
    setDialogName,
  };
};
